import { gql } from '@apollo/client';
import { Button, Col, Form, Input, InputNumber, Row, Switch } from 'antd';
import FormLabel from 'components/FormLabel';
import { useMutation } from 'hooks/apollo';
import React, { useMemo } from 'react';

import globalMessages from 'translations/messages';
import { useHistory } from 'react-router';
import useDataSource from 'hooks/useDataSource';
import Page from 'components/Page';
import useGlobalContext from 'hooks/useGlobalContext';
import useFormatMessage from 'hooks/useFormatMessage';
import useUser from 'hooks/useUser';
import CurrencySelect from 'components/CurrencySelect';
import TimezoneSelect from 'components/TimezoneSelect';
import SurveyCategorySelect from 'components/SurveyCategorySelect';
import messages from '../messages';

const extraFields = `
  isTest
  pipedriveDealId
  website { 
    id 
    categories { id }
    matomoId 
    matomoSettings { id currency timezone } 
  }
`;

const UPDATE_DATA_SOURCE = gql`
  mutation($id: ID!, $input: DataSourceInput!) {
    updateDataSource(id: $id, input: $input) {
      id
      title
      ${extraFields}
    }
  }
`;

const REMOVE_DATA_SOURCE = gql`
  mutation($id: ID!) {
    removeDataSource(id: $id) {
      id
    }
  }
`;

const DataSourceGeneralSettings = () => {
  const { dataSource, loading } = useDataSource({ extraFields });
  const [updateDataSource, { loading: updating }] = useMutation(UPDATE_DATA_SOURCE);
  const [removeDataSource] = useMutation(REMOVE_DATA_SOURCE);
  const history = useHistory();
  const { modal } = useGlobalContext();
  const $t = useFormatMessage();
  const { isSuperUser } = useUser();

  const initialValues = useMemo(() => dataSource && ({
    ...dataSource,
    isTest: dataSource.isTest ?? false,
    website: dataSource.website && {
      ...dataSource.website,
      categoryIds: dataSource.website.categories.map(({ id }) => id),
    },
  }), [dataSource]);

  const onSubmit = (values) => updateDataSource({ variables: { id: dataSource.id, input: values } });

  const onRemove = async () => {
    modal.confirm({
      title: $t(globalMessages.confirmDelete, { title: dataSource.title }),
      okText: $t(globalMessages.delete),
      okType: 'danger',
      confirmLoading: true,
      onOk: async () => {
        await removeDataSource({ variables: { id: dataSource.id } });
        history.push('/data-sources');
      },
    });
  };

  return (
    <Page loading={loading}>
      <Page.Content title="General settings">
        {dataSource && (
          <Form layout="vertical" onFinish={onSubmit} initialValues={initialValues}>
            <Form.Item
              name="title"
              label={<FormLabel label={globalMessages.title} description={messages.titleDescription} />}
            >
              <Input />
            </Form.Item>
            {dataSource.website && (
              <>
                <Form.Item
                  name={['website', 'categoryIds']}
                  label="Industry"
                >
                  <SurveyCategorySelect mode="multiple" />
                </Form.Item>
                <Form.Item
                  name={['website', 'matomoSettings', 'currency']}
                  label={<FormLabel label={messages.currencyLabel} description={messages.currencyDescription} />}
                >
                  <CurrencySelect />
                </Form.Item>
                <Form.Item
                  name={['website', 'matomoSettings', 'timezone']}
                  label={<FormLabel label={messages.timezoneLabel} description={messages.timezoneDescription} />}
                >
                  <TimezoneSelect />
                </Form.Item>
              </>
            )}
            {isSuperUser && (
              <Row gutter={16}>
                <Col>
                  <Form.Item
                    name="pipedriveDealId"
                    label="Pipedrive Deal ID"
                  >
                    <InputNumber />
                  </Form.Item>
                </Col>
              </Row>
            )}
            {dataSource.website && (
              <Form.Item
                name="isTest"
                valuePropName="checked"
                label={<FormLabel label={messages.isTestLabel} description={messages.isTestDescription} />}
              >
                <Switch />
              </Form.Item>
            )}
            <Page.Footer>
              <Button type="primary" htmlType="submit" loading={updating}>
                Save
              </Button>
            </Page.Footer>
          </Form>
        )}
      </Page.Content>
      <Page.Content title="Danger zone">
        <Button danger type="primary" onClick={onRemove}>
          Delete data source
        </Button>
      </Page.Content>
    </Page>
  );
};

export default DataSourceGeneralSettings;
